import React, { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { SportIcon } from '../utils/sportIcons';
import Modal from './Modal';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string | null;
}

interface DropdownProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder = '',
  title,
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const selectedOption = options.find((option) => option.value === value);
  const triggerLabel = selectedOption?.label ?? placeholder;
  const triggerIcon = selectedOption?.icon;
  const modalTitle = title ?? placeholder;

  useEffect(() => {
    if (!open || isMobile) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isMobile]);

  const handleSelect = (option: DropdownOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  const renderOptionContent = (option: DropdownOption) => (
    <span className="dropdown__option-content">
      <SportIcon name={option.icon} size={16} />
      <span>{option.label}</span>
    </span>
  );

  const renderOptions = (listClassName: string, optionClassName: string) => (
    <ul className={listClassName} role="listbox">
      {options.map((option) => (
        <li
          key={`${option.value}-${option.label}`}
          role="option"
          aria-selected={value === option.value}
          aria-disabled={option.disabled || undefined}
          className={`${optionClassName}${
            option.disabled ? ' dropdown__option--disabled' : ''
          }${value === option.value ? ' dropdown__option--selected' : ''}`}
          onClick={() => handleSelect(option)}
        >
          {renderOptionContent(option)}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      ref={rootRef}
      className={`dropdown${disabled ? ' dropdown--disabled' : ''}`}
    >
      <button
        type="button"
        id={id}
        className={`dropdown__trigger${className ? ` ${className}` : ''}`}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup={isMobile ? 'dialog' : 'listbox'}
        onClick={() => {
          if (!disabled) {
            setOpen((current) => !current);
          }
        }}
      >
        <span className="dropdown__trigger-content">
          <SportIcon name={triggerIcon} size={16} />
          <span>{triggerLabel}</span>
        </span>
      </button>
      {open && !isMobile && renderOptions('dropdown__list', 'dropdown__option')}
      {isMobile && (
        <Modal
          isOpen={open}
          onClose={() => setOpen(false)}
          title={modalTitle}
          size="medium"
        >
          {renderOptions('dropdown__modal-list', 'dropdown__option dropdown__option--modal')}
        </Modal>
      )}
    </div>
  );
};

export default Dropdown;
